import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { 
  HiOutlinePrinter, HiOutlineChevronLeft, HiOutlineMapPin, 
  HiOutlineUser, HiOutlineInformationCircle, HiOutlineShieldCheck,
  HiOutlinePhone, HiOutlineCurrencyRupee, HiOutlineCalendar,
  HiOutlineEnvelope, HiOutlineBuildingOffice, HiOutlineTicket
} from "react-icons/hi2";
import { baseurl } from "../../baseurl";
import "../../asset/css/invoice.css";

const HotelInvoice = () => {
  const { ticketId } = useParams(); // This is the booking_reference
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBookingDetails = async () => {
      try {
        // Updated endpoint as requested
        const response = await axios.get(`${baseurl}/getBookingByReferenceUser/${ticketId}`);
        if (response.data.status === "success") {
          setBooking(response.data.booking);
        }
      } catch (error) {
        console.error("Error fetching hotel booking:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBookingDetails();
  }, [ticketId]);

  const handlePrint = () => window.print();

  if (loading) return <div className="text-center py-5"><h4>Generating your voucher...</h4></div>;
  if (!booking) return <div className="text-center py-5"><h4>Booking not found.</h4></div>;

  // Shortcuts to nested data
  const details = booking.response_payload?.CommitBooking?.BookingDetails;
  const passengers = booking.additional_data?.RoomDetails[0]?.PassengerDetails || [];
  const payment = booking.payment_details;

  return (
    <div className="invoice-wrapper py-4 px-2" style={{marginTop: "100px"}}>
      <style>
        {`
          @media print {
            body { visibility: hidden; background: white !important; }
            .no-print { display: none !important; }
            #printable-invoice { 
              visibility: visible; 
              position: absolute; 
              left: 0; top: 0; width: 100%; 
              margin: 0; padding: 0;
              box-shadow: none !important;
              border: none !important;
            }
            @page { margin: 0.8cm; }
          }
          .op-7 { opacity: 0.7; }
          .bg-teal { background-color: #166070 !important; }
          .text-teal { color: #166070 !important; }
        `}
      </style>

      {/* Navigation */}
      <div className="container no-print mb-3">
        <div className="d-flex justify-content-between align-items-center">
          <button className="btn btn-light shadow-sm btn-sm" onClick={() => window.history.back()}>
            <HiOutlineChevronLeft /> Back
          </button>
          <button className="btn btn-teal text-white shadow-sm btn-sm px-4" onClick={handlePrint}>
            <HiOutlinePrinter className="me-1" /> Print Invoice
          </button>
        </div>
      </div>

      <div className="invoice-card mx-auto shadow-lg bg-white" id="printable-invoice" style={{ maxWidth: '850px' }}>
        {/* Branding Header */}
        <div className="p-4 border-bottom bg-light">
          <div className="row align-items-center">
            <div className="col-6">
              <h4 className="fw-bold text-teal mb-0">TopAirView</h4>
              <p className="text-muted small mb-0">Solutions Limited | Hotel Invoice</p>
            </div>
            <div className="col-6 text-end">
              <div className="small text-muted" style={{ fontSize: '11px' }}>
                <HiOutlineEnvelope className="me-1" /> a2zyatra2012@gmail.com<br />
                <HiOutlineBuildingOffice className="me-1" /> Haridwar, UK (249401)
              </div>
            </div>
          </div>
        </div>

        {/* Main Status Header */}
        <div className="bg-teal p-4 text-white">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h3 className="fw-bold mb-0 text-uppercase">Hotel Invoice</h3>
              <span className="op-7">Reference: {booking.booking_reference}</span>
              <div className="mt-2">
                <span className="badge bg-white text-teal px-3 py-2">
                  {details?.booking_status || booking.status}
                </span>
              </div>
            </div>
            <div className="text-end">
              <p className="mb-0 op-7">Confirmation No.</p>
              <h4 className="fw-bold mb-0">{details?.ConfirmationNo || 'N/A'}</h4>
              <p className="small mb-0 op-7">Booking ID: {details?.BookingId}</p>
            </div>
          </div>
        </div>

        <div className="p-4">
          <div className="row g-4">
            {/* Left Column: Guest & Hotel Info */}
            <div className="col-md-7">
              <section className="mb-4">
                <h6 className="section-title text-teal fw-bold border-bottom pb-2">
                  <HiOutlineUser className="me-2" /> Guest Information
                </h6>
                <div className="bg-light rounded p-3">
                  {passengers.map((pax, idx) => (
                    <div key={idx} className={`d-flex justify-content-between ${idx !== 0 ? 'mt-2 pt-2 border-top' : ''}`}>
                      <div>
                        <span className="fw-bold">{pax.Title} {pax.FirstName} {pax.LastName}</span>
                        {pax.LeadPassenger && <span className="badge bg-teal ms-2 small" style={{fontSize:'9px'}}>Lead</span>}
                      </div>
                      <span className="text-muted small">Age: {pax.Age}</span>
                    </div>
                  ))}
                  <div className="mt-3 small text-muted">
                    <HiOutlinePhone className="me-1"/> {passengers[0]?.Phoneno} | <HiOutlineEnvelope className="me-1"/> {passengers[0]?.Email}
                  </div>
                </div>
              </section>

              <section className="mb-4">
                <h6 className="section-title text-teal fw-bold border-bottom pb-2">
                  <HiOutlineInformationCircle className="me-2" /> Booking Details
                </h6>
                <div className="row mt-2">
                  <div className="col-6">
                    <small className="text-muted d-block">Service Type</small>
                    <span className="fw-bold">Hotel Reservation</span>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-block">Booking Date</small>
                    <span className="fw-bold">{new Date(booking.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="mt-3 p-3 border rounded border-dashed">
                  <HiOutlineShieldCheck className="text-success me-2"/> 
                  <span className="small text-muted">Please present this voucher and a valid Photo ID at the time of Check-in.</span>
                </div>
              </section>
            </div>

            {/* Right Column: Payment & Dates */}
            <div className="col-md-5">
              <section className="mb-4">
                <h6 className="section-title text-teal fw-bold border-bottom pb-2">
                  <HiOutlineCurrencyRupee className="me-2" /> Payment Summary
                </h6>
                <div className="card shadow-sm border-0 bg-light">
                  <div className="card-body">
                    <div className="d-flex justify-content-between mb-2">
                      <span>Total Amount Paid</span>
                      <span className="fw-bold">₹{booking.amount}</span>
                    </div>
                    <div className="d-flex justify-content-between small text-muted mb-2">
                      <span>Currency</span>
                      <span>{booking.currency}</span>
                    </div>
                    <hr />
                    <div className="small">
                      <div className="text-muted mb-1">Transaction Details:</div>
                      <div className="d-flex justify-content-between text-truncate">
                        <span>Gateway ID:</span>
                        <span className="ms-2">{payment?.razorpay_payment_id || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h6 className="section-title text-teal fw-bold border-bottom pb-2">
                  <HiOutlineCalendar className="me-2" /> Important Dates
                </h6>
                <div className="p-3 border rounded">
                  <div className="mb-2">
                    <small className="text-muted d-block">Check-in Date</small>
                    <span className="fw-bold">{new Date(booking.created_at).toDateString()} (Est.)</span>
                  </div>
                  <div className="text-muted small italic">
                    *Refer to hotel policy for standard check-in/out times.
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>

        {/* Technical Footer */}
        <div className="px-4 py-3 border-top d-flex justify-content-between small text-muted bg-light" style={{ fontSize: '10px' }}>
           <span><strong>Order ID:</strong> {payment?.razorpay_order_id}</span>
           <span><strong>Reference:</strong> {booking.booking_reference}</span>
           <span><strong>Verified:</strong> {new Date(payment?.verified_at).toLocaleString()}</span>
        </div>

        {/* Legal Footer */}
        <div className="p-4 bg-dark text-white-50" style={{ fontSize: '10px' }}>
          <div className="row align-items-center">
            <div className="col-8">
              <p className="mb-1 fw-bold text-white">Guest Policy & Information:</p>
              <ul className="ps-3 mb-0">
                <li>Primary guest must be at least 18 years of age.</li>
                <li>Check-in begins at 12:00 PM (Local Time) unless specified otherwise.</li>
                <li>TopAirView acts as an agent; actual service is provided by the hotel.</li>
              </ul>
            </div>
            <div className="col-4 text-end">
              <p className="mb-0 text-white"><strong>TopAirView Solutions Ltd</strong></p>
              <p className="mb-0 small">Secure Booking System</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotelInvoice;